// Portal theme tokens — the single source of truth for both themes.
// See PORTAL-THEME-PLAN.md §2.1a for the locked value decisions (2026-08-17):
// spec token NAMES, existing-app VALUES for light (sole new color: --page
// #F4F6FA, user-approved), spec's approved dark set for dark, gold/status/
// category substitutions from the live palette. Everything else in the app
// consumes these through one of two generated artifacts:
//   - CSS custom properties (themeGlobalStyles) for sx/Tailwind/Sass markup
//   - buildAppTheme() in src/theme.ts for MUI (real hex — MUI 5 throws on var())
// Never hand a raw hex to a component when a token exists for its ROLE, and
// never mint a new token for a single call site — tokens are semantic roles,
// not color aliases.

export type ThemeMode = 'light' | 'dark'

export const LIGHT = {
  /* foundation */
  page: '#F4F6FA', // approved exception — the one new light color
  surface: '#FFFFFF',
  /* surface2/track were the ORIGINAL warm neutrals (#F4F2EE / #EDEAE5). Against
     the cool --page they sat ~1.03 apart in contrast but on the opposite side of
     neutral — a 12-point temperature swing at the same lightness, which reads as
     a dirty patch rather than a distinct layer, everywhere a surface-2 element
     touches the canvas (the Selected band, search inputs, filter pills).
     Both are now derived from --page's own channel offsets (+2 green, +6 blue)
     stepped down, so the neutral ramp is one coherent cool family and each step
     is actually visible: page 0.92 -> surface-2 0.84 -> track 0.77 luminance.
     They move TOGETHER on purpose: CHANNEL_PILL_SX deliberately puts a --track
     pill beside a --surface-2 pill, so cooling only one would just relocate the
     clash. --tint stays warm — it is the gold BRAND wash, not a neutral. */
  surface2: '#EAECF0',
  topbar: '#FFFFFF',
  track: '#E2E4E8',
  border: '#ECECEC',
  /* content */
  text: '#1C1A17',
  text2: '#727272',
  muted: '#9B958C',
  /* brand — gold family, constant across themes (existing primary family) */
  accent: '#A58450',
  accentDark: '#B79C73',
  accentText: '#805C22',
  tint: '#F2EBE2',
  /* media — video player plate + on-thumbnail chip scrim (same in both themes:
     they sit on imagery, not on the theme canvas) */
  player: '#0A0F18',
  scrim: 'rgba(10,15,24,.72)',
  /* status — existing chip pairs */
  up: '#085041',
  upBg: '#E1F5EE',
  down: '#791F1F',
  downBg: '#FCEBEB',
  /* navigation rail — spec navy set, deliberately identical in both themes */
  rail: '#131B29',
  railHover: '#1D2839',
  railActive: '#26344B',
  railText: '#EAEDF2',
  railText2: '#A9B2C1',
  railMuted: '#6E7889',
  railAccent: '#B79C73',
  railRule: 'rgba(255,255,255,.08)',
  /* NOTE: there are deliberately no --c-* data-viz tokens here. The dashboard's
     category hues live in internalDashboard/helper.tsx and are consumed from
     there; duplicating them as tokens shipped 14 dead declarations in the global
     stylesheet and implied a source of truth this file does not actually own. */
  /* elevation */
  sh1: '0 1px 2px rgba(20,20,20,.05)',
  sh2: '0 1px 2px rgba(20,20,20,.05), 0 8px 24px rgba(20,20,20,.06)',
  shTopbar: '0 1px 2px rgba(20,20,20,.04), 0 2px 12px rgba(20,20,20,.045)',
  railShadow: '2px 0 20px rgba(0,0,0,.28)',
} as const

export type TokenName = keyof typeof LIGHT

// Record<TokenName, string> makes key parity a compile-time guarantee:
// a token missing here (or added only here) fails tsc.
export const DARK: Record<TokenName, string> = {
  /* foundation */
  page: '#0D131F',
  surface: '#18212F',
  surface2: '#1F2A3B',
  topbar: '#131B29',
  track: '#232E40',
  border: 'rgba(255,255,255,.08)',
  /* content */
  text: '#EAEDF2',
  text2: '#A9B2C1',
  muted: '#6E7889',
  /* brand — constant gold; text/tint variants adjust for the dark canvas */
  accent: '#A58450',
  accentDark: '#B79C73',
  accentText: '#B79C73',
  tint: 'rgba(183,156,115,.16)',
  /* media — identical to light (sits on imagery) */
  player: LIGHT.player,
  scrim: LIGHT.scrim,
  /* status — existing greens/reds as translucent washes */
  up: '#3CD670',
  upBg: 'rgba(60,214,112,.15)',
  down: '#EB3E33',
  downBg: 'rgba(235,62,51,.15)',
  /* navigation rail — identical to light */
  rail: LIGHT.rail,
  railHover: LIGHT.railHover,
  railActive: LIGHT.railActive,
  railText: LIGHT.railText,
  railText2: LIGHT.railText2,
  railMuted: LIGHT.railMuted,
  railAccent: LIGHT.railAccent,
  railRule: LIGHT.railRule,
  /* elevation */
  sh1: '0 1px 2px rgba(0,0,0,.45)',
  sh2: '0 1px 2px rgba(0,0,0,.5), 0 8px 24px rgba(0,0,0,.4)',
  shTopbar: '0 1px 2px rgba(0,0,0,.55), 0 4px 18px rgba(0,0,0,.45)',
  railShadow: LIGHT.railShadow,
}

export const TOKENS: Record<ThemeMode, Record<TokenName, string>> = {
  light: LIGHT,
  dark: DARK,
}

// camelCase token name -> CSS custom property name
// (surface2 -> --surface-2, railText2 -> --rail-text-2, cNewBrands -> --c-new-brands)
export const cssVarName = (name: TokenName): string =>
  `--${name.replace(/([a-z])([A-Z0-9])/g, '$1-$2').toLowerCase()}`

const declarations = (tokens: Record<TokenName, string>, onlyDiffering = false): string =>
  (Object.keys(LIGHT) as TokenName[])
    .filter((name) => !onlyDiffering || tokens[name] !== LIGHT[name])
    .map((name) => `  ${cssVarName(name)}: ${tokens[name]};`)
    .join('\n')

// Global stylesheet: light set on :root, dark overrides only what changes
// (spec §01 structure). color-scheme flips native scrollbars/inputs. The
// focus rule is the spec's one-rule-covers-both-themes gold ring. The
// theme-switching class is added transiently (220ms) by ThemeRoot on flips,
// scoped to color-bearing properties only and disabled for reduced motion.
export const themeGlobalStyles = `
:root {
${declarations(LIGHT)}
  color-scheme: light;

  /* Legacy Figma "--Base-*" properties. ~74 call sites across the app were
     written as var(--Base-900, #444) but the properties were never declared,
     so every one silently fell back to its literal and stayed light-locked.
     Declaring them here with EXACTLY the historical fallback values changes
     nothing in light mode, and the dark block below finally lets those call
     sites flip. Do not add new --Base-* usages: they are a compatibility
     shim, not part of the token contract. */
  --Base-0: #FFFFFF;
  --Base-24: #F4F4F4;
  --Base-100: #DFDEDE;
  --Base-300: #B0B0B0;
  --Base-600: #727272;
  --Base-900: #444444;
  --Base-1000: #000000;
}

[data-theme="dark"] {
${declarations(DARK, true)}
  color-scheme: dark;

  /* Dark-only override for the project timeline's "upcoming milestone" marker.
     Declared ONLY here so light keeps its literal #D4C5B0 via the var()
     fallback at the call site — the tan reads as a warm pending state on white
     but glares on the dark canvas. Not a general token; do not reuse. */
  --timeline-upcoming: var(--muted);

  /* Dark remaps for the client-tool palette (src/components/ProposalFlow/
     theme.ts): the PDP audit / proposal / prep-pack tools are ALSO embedded on
     the public marketing site, where they must keep their literal warm palette
     pixel-for-pixel. Their constants are written var(--tool-*, <literal>) and
     these properties are declared ONLY here, so light (and every public page,
     which never enters a dark scope) keeps the literal via the fallback while
     the portal's dark mode resolves to the real tokens. Consume them through
     theme.ts only — never reference --tool-* at a call site. */
  --tool-text: var(--text);
  --tool-text-2: var(--text-2);
  --tool-muted: var(--muted);
  --tool-border: var(--border);
  --tool-card: var(--surface);
  --tool-surface-2: var(--surface-2);
  --tool-page: var(--page);
  --tool-tint: var(--tint);
  --tool-up: var(--up);
  --tool-up-bg: var(--up-bg);
  --tool-down: var(--down);
  --tool-down-bg: var(--down-bg);
  /* "Ink" buttons (dark plate, white label) invert on the dark canvas. */
  --tool-ink: var(--text);
  --tool-ink-text: var(--page);

  /* Legacy shim mapped onto real tokens for dark.
     --Base-0 deliberately STAYS #FFFFFF: 9 of its 11 call sites use it as the
     label color on a gold button (text-on-color), where white is correct in
     both themes; only 2 used it as a surface, and those now name --surface
     directly. Mapping it to --surface turned those button labels dark navy. */
  --Base-0: #FFFFFF;
  --Base-24: var(--surface-2);
  --Base-100: var(--border);
  --Base-300: var(--muted);
  --Base-600: var(--text-2);
  --Base-900: var(--text);
  --Base-1000: var(--text);
}

[data-theme="dark"] body {
  background: var(--page);
  /* Un-styled text (plain <td>/<span>/<p> with no color class) otherwise
     inherits the browser's black. Dark-only so light keeps its defaults. */
  color: var(--text);
}

:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}

html.theme-switching,
html.theme-switching * {
  transition:
    background-color 220ms cubic-bezier(.2,.7,.2,1),
    background 220ms cubic-bezier(.2,.7,.2,1),
    border-color 220ms cubic-bezier(.2,.7,.2,1),
    color 220ms cubic-bezier(.2,.7,.2,1),
    box-shadow 220ms cubic-bezier(.2,.7,.2,1),
    fill 220ms cubic-bezier(.2,.7,.2,1),
    stroke 220ms cubic-bezier(.2,.7,.2,1) !important;
}

@media (prefers-reduced-motion: reduce) {
  html.theme-switching,
  html.theme-switching * {
    transition: none !important;
  }
}

.body-surface {
  background: var(--surface) !important;
}

/* The navigation rail is navy in BOTH themes, so it must be styled as a single
   theme-independent surface. Most of it already uses the constant --rail-*
   tokens, but a few spots legitimately want the gold family (the Beta pill, the
   account avatar) and the raised-shadow token — and those DO flip. In light
   that painted the opaque cream --tint (#F2EBE2) and the deep --accent-text
   (#A07B2E) onto navy, which read as bright blobs.

   Re-declaring just those tokens on the rail pins them to their dark values in
   both themes: dark is unchanged, light now matches it, and the components keep
   using plain token names with no literals and no per-theme branching.

   Scoped to rail-OWNED classes, never the framework-wide .MuiDrawer-paper: any
   future non-rail Drawer would otherwise inherit a translucent gold --tint that
   is invisible on white and a black --sh-2 built for a dark canvas. Rail
   surfaces that render OUTSIDE the drawer (the collapsed-rail flyout portals to
   <body>) must opt in by name — see .rail-flyout. */
.rail-surface,
.rail-flyout {
  --tint: ${DARK.tint};
  --accent-text: ${DARK.accentText};
  --sh-2: ${DARK.sh2};
}

/* NOTE: product and brand photography is deliberately NOT dimmed in dark mode.
   Those images are user-supplied and frequently have a white background baked
   in, but dimming them muted real brand and product artwork, which looked worse
   than the white plate it was meant to solve. Leave uploaded imagery at full
   fidelity. (The opaque placeholder below is a shipped asset, not user content,
   so it is still themed.) */

/* The "no video yet" thumbnail is an OPAQUE light-grey PNG (assets/
   video_placeholder.png) used at 8 call sites, so it cannot be themed by any
   container. Targeting it by filename keeps this to one rule and zero call-site
   edits — CRA preserves the original name in the hashed asset URL. Plain
   darkening is used rather than invert() so the play glyph stays LIGHTER than
   its plate, exactly as the artwork intends. */
[data-theme="dark"] img[src*="video_placeholder"] {
  filter: brightness(0.5) saturate(0.8);
}

`
